import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Footprints,
  MapPin,
  LogOut,
  X,
  Bell,
  ChevronRight,
  Repeat,
  CheckCircle2,
  Loader2,
  PlayCircle,
  Clock,
} from "lucide-react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { appConfig, formatHora } from "../../data/mock";
import { supabase } from "../../lib/supabase";
import { toast } from "sonner";

const EMPRESA_ID    = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const ROLES_SUP     = ["supervisor", "admin", "liberty", "esquematiza", "admin_master"];
const POLL_INTERVAL = 30000;

export default function PlantaoHome() {
  const { user, getById, postos, condominios, fotoPerfilUrl, avisosNaoLidos } = useApp();
  const navigate = useNavigate();
  const isSupervisor = ROLES_SUP.includes(user?.role);

  const [modalLista,   setModalLista]   = useState(null);
  const [listaItems,   setListaItems]   = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);

  const abrirLista = async (tipo) => {
    setModalLista(tipo);
    setLoadingLista(true);
    setListaItems([]);

    const hoje    = new Date(); hoje.setHours(0, 0, 0, 0);
    const hojeISO = hoje.toISOString();
    const nome    = user?.nome;
    let q;

    if (tipo === "rondas") {
      q = supabase.from("liberty_ronda_pontos").select("id, vigilante_nome, ponto, created_at").eq("empresa_id", EMPRESA_ID).gte("created_at", hojeISO).order("created_at", { ascending: false });
      if (!isSupervisor && nome) q = q.ilike("vigilante_nome", nome);
    } else if (tipo === "ocorrencias") {
      q = supabase.from("liberty_ocorrencias").select("id, titulo, categoria, prioridade, vigilante_nome, created_at").eq("empresa_id", EMPRESA_ID).gte("created_at", hojeISO).order("created_at", { ascending: false });
      if (!isSupervisor && nome) q = q.ilike("vigilante_nome", nome);
    }

    const { data } = await q;
    setListaItems(data || []);
    setLoadingLista(false);
  };

  const [resumo, setResumo] = useState({ rondas: 0, ocorrencias: 0 });
  const [avisosRecentes,    setAvisosRecentes]    = useState([]);
  const [ultimaPassagem,    setUltimaPassagem]    = useState(undefined); // undefined = carregando
  const [passagemPendente,  setPassagemPendente]  = useState(null);
  const [loadingReceber,    setLoadingReceber]    = useState(false);
  const [plantaoAtivo,      setPlantaoAtivo]      = useState(null);
  const [segundos,          setSegundos]          = useState(0);
  const [iniciandoPlantao,  setIniciandoPlantao]  = useState(false);
  const [rondasIntel,       setRondasIntel]        = useState(null);
  const timerRef         = useRef(null);
  const autoEncerrarRef  = useRef(null);

  useEffect(() => {
    const nome = user?.nome;

    const buscar = async () => {
      const hoje    = new Date(); hoje.setHours(0, 0, 0, 0);
      const hojeISO = hoje.toISOString();

      let qRondas = supabase.from("liberty_ronda_pontos").select("id", { count: "exact", head: true }).eq("empresa_id", EMPRESA_ID).gte("created_at", hojeISO);
      if (!isSupervisor && nome) qRondas = qRondas.ilike("vigilante_nome", nome);

      let qOc = supabase.from("liberty_ocorrencias").select("id", { count: "exact", head: true }).eq("empresa_id", EMPRESA_ID).gte("created_at", hojeISO);
      if (!isSupervisor && nome) qOc = qOc.ilike("vigilante_nome", nome);

      const [r1, r2] = await Promise.all([qRondas, qOc]);

      setResumo({
        rondas:      r1.count ?? 0,
        ocorrencias: r2.count ?? 0,
      });
    };

    buscar();
    const t = setInterval(buscar, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [isSupervisor, user?.nome]); // eslint-disable-line

  // Verificar plantão ativo
  useEffect(() => {
    if (!user?.id || isSupervisor) return;
    const buscar = async () => {
      const { data } = await supabase
        .from("liberty_plantoes")
        .select("id, inicio")
        .eq("empresa_id", EMPRESA_ID)
        .eq("vigilante_id", user.id)
        .is("fim", null)
        .order("inicio", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        setPlantaoAtivo(data);
        setSegundos(Math.floor((Date.now() - new Date(data.inicio).getTime()) / 1000));
      }
    };
    buscar();
  }, [user?.id, isSupervisor]); // eslint-disable-line

  // Timer do plantão ativo
  useEffect(() => {
    if (!plantaoAtivo) return;
    timerRef.current = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [plantaoAtivo]);

  const iniciarPlantao = async () => {
    setIniciandoPlantao(true);

    // Encerra quaisquer plantões abertos do mesmo vigilante antes de abrir novo
    const { data: abertos } = await supabase
      .from("liberty_plantoes")
      .select("id, inicio")
      .eq("empresa_id", EMPRESA_ID)
      .eq("vigilante_id", user?.id)
      .is("fim", null);

    for (const p of (abertos || [])) {
      const duracaoMin = Math.round((Date.now() - new Date(p.inicio).getTime()) / 60000);
      await supabase.from("liberty_plantoes").update({
        fim:             new Date().toISOString(),
        duracao_minutos: duracaoMin,
      }).eq("id", p.id);
    }

    const { data, error } = await supabase
      .from("liberty_plantoes")
      .insert({
        empresa_id:     EMPRESA_ID,
        vigilante_id:   user?.id,
        vigilante_nome: user?.nome,
        dispositivo:    navigator.userAgent?.slice(0, 150) || null,
      })
      .select("id, inicio")
      .single();

    setIniciandoPlantao(false);
    if (!error && data) {
      setPlantaoAtivo(data);
      setSegundos(0);
      toast.success("Plantão iniciado!");
    } else if (error) {
      toast.error("Erro ao iniciar plantão. Tente novamente.");
    }
  };

  // Auto-encerrar plantão após 14h sem encerramento manual
  useEffect(() => {
    if (!plantaoAtivo) { clearInterval(autoEncerrarRef.current); return; }
    const check = async () => {
      const horasAbertas = (Date.now() - new Date(plantaoAtivo.inicio).getTime()) / 3600000;
      if (horasAbertas >= 14) {
        const duracaoMin = Math.round((Date.now() - new Date(plantaoAtivo.inicio).getTime()) / 60000);
        await supabase.from("liberty_plantoes").update({
          fim:             new Date().toISOString(),
          duracao_minutos: duracaoMin,
        }).eq("id", plantaoAtivo.id);
        clearInterval(autoEncerrarRef.current);
        clearInterval(timerRef.current);
        setPlantaoAtivo(null);
        setSegundos(0);
        toast.info("Plantão encerrado automaticamente após 14 horas.");
      }
    };
    check();
    autoEncerrarRef.current = setInterval(check, 60000);
    return () => clearInterval(autoEncerrarRef.current);
  }, [plantaoAtivo]); // eslint-disable-line

  const fmtElapsed = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
  };

  // Avisos recentes (2 mais recentes)
  useEffect(() => {
    const buscar = async () => {
      const { data } = await supabase
        .from("liberty_avisos")
        .select("id, titulo, prioridade, created_at")
        .eq("empresa_id", EMPRESA_ID)
        .order("created_at", { ascending: false })
        .limit(2);
      setAvisosRecentes(data || []);
    };
    buscar();
  }, []); // eslint-disable-line

  // Última passagem recebida por este vigilante
  useEffect(() => {
    if (!user?.nome) return;
    const buscar = async () => {
      const { data } = await supabase
        .from("liberty_passagem_turno")
        .select("id, de_vigilante_nome, titulo, recebido_em")
        .eq("empresa_id", EMPRESA_ID)
        .ilike("para_vigilante_nome", user.nome)
        .not("recebido_em", "is", null)
        .order("recebido_em", { ascending: false })
        .limit(1)
        .maybeSingle();
      setUltimaPassagem(data || null);
    };
    buscar();
  }, [user?.nome]); // eslint-disable-line

  // Passagem pendente
  useEffect(() => {
    if (!user?.nome) return;
    const buscar = async () => {
      const limite = new Date();
      limite.setHours(limite.getHours() - 48);
      const { data } = await supabase
        .from("liberty_passagem_turno")
        .select("id, de_vigilante_nome, titulo, prioridade, created_at")
        .eq("empresa_id", EMPRESA_ID)
        .ilike("para_vigilante_nome", user.nome)
        .is("recebido_em", null)
        .gte("created_at", limite.toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setPassagemPendente(data || null);
    };
    buscar();
  }, [user?.nome]); // eslint-disable-line

  // Inteligência de ronda — calcula próxima ronda com base no intervalo médio histórico
  useEffect(() => {
    if (!user?.nome || isSupervisor) return;
    const buscar = async () => {
      const { data } = await supabase
        .from("liberty_rondas")
        .select("id, created_at")
        .eq("empresa_id", EMPRESA_ID)
        .ilike("responsavel", user.nome)
        .order("created_at", { ascending: false })
        .limit(10);

      if (!data || data.length === 0) {
        setRondasIntel({ semHistorico: true, ultimaRondaAt: null, proximaRondaAt: null });
        return;
      }

      const ultimaRondaAt = new Date(data[0].created_at);

      if (data.length === 1) {
        setRondasIntel({
          semHistorico: false,
          ultimaRondaAt,
          proximaRondaAt: new Date(ultimaRondaAt.getTime() + 60 * 60 * 1000),
        });
        return;
      }

      const ts = data.map(r => new Date(r.created_at).getTime()).reverse();
      const diffs = ts.slice(1).map((t, i) => t - ts[i]);
      const avgMs = diffs.reduce((a, b) => a + b, 0) / diffs.length;
      setRondasIntel({
        semHistorico: false,
        ultimaRondaAt,
        proximaRondaAt: new Date(ultimaRondaAt.getTime() + avgMs),
      });
    };
    buscar();
    const t = setInterval(buscar, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [user?.nome, isSupervisor]); // eslint-disable-line

  const receberPassagem = async () => {
    if (!passagemPendente) return;
    setLoadingReceber(true);
    await supabase
      .from("liberty_passagem_turno")
      .update({
        recebido_por_nome: user?.nome,
        recebido_por_id:   user?.id,
        recebido_em:       new Date().toISOString(),
      })
      .eq("id", passagemPendente.id);
    setPassagemPendente(null);
    setLoadingReceber(false);
    toast.success("Passagem recebida com sucesso!");
  };

  const posto = getById(postos, user?.postoId);
  const condo = getById(condominios, user?.condominioId);

  const iniciais = (user?.usuarioNome || user?.nome || "VG")
    .split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("");

  const fmtHora = (iso) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const titulosModal = { rondas: "Rondas do plantão", ocorrencias: "Ocorrências do plantão" };
  const vazioModal   = { rondas: "Nenhuma ronda registrada hoje", ocorrencias: "Nenhuma ocorrência hoje" };

  // Status da próxima ronda (recalculado a cada render via timer de segundos)
  const _agora       = Date.now();
  const _proximaMs   = rondasIntel?.proximaRondaAt?.getTime();
  const rondaAtrasada    = !!(_proximaMs && _agora > _proximaMs + 15 * 60 * 1000);
  const rondaRestanteMin = _proximaMs ? Math.ceil((_proximaMs - _agora) / 60000) : null;

  return (
    <MobileLayout title="Início">
      <div className="flex flex-col gap-3 h-full">

        {/* Card perfil do vigilante → /perfil */}
        <button
          type="button"
          onClick={() => navigate("/perfil")}
          className="flex-none rounded-2xl p-3 w-full text-left active:scale-[0.985] transition-transform"
          style={{ background: "#ffffff", border: "1px solid #e8eaf0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-50" />
                <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-semibold" style={{ color: "#10B981" }}>Em serviço</span>
            </div>
            <span className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>
              {formatHora(new Date().toISOString())}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" style={{ border: "2px solid #e8eaf0" }}>
              {fotoPerfilUrl ? (
                <img src={fotoPerfilUrl} alt="foto" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[13px] font-bold" style={{ background: "#08111F", color: "#3B82F6" }}>
                  {iniciais}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#94a3b8" }}>
                {isSupervisor ? "Supervisor logado" : "Vigilante logado"}
              </p>
              <h2 className="font-bold text-[16px] leading-tight text-[#0f172a] truncate">
                {user?.usuarioNome || user?.nome || "Usuário"}
              </h2>
              <p className="flex items-center gap-1.5 text-[11px] mt-0.5" style={{ color: "#64748b" }}>
                <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: "#3B82F6" }} />
                <span className="truncate">
                  {posto?.nome || (isSupervisor ? "Liberty Place" : "Posto não informado")} ·{" "}
                  <span translate="no">{condo?.nome || appConfig.condominioPadrao}</span>
                </span>
              </p>
            </div>
          </div>
        </button>

        {/* Rondas hoje */}
        <MiniMetric icon={Footprints} label="Rondas hoje" value={resumo.rondas} onClick={() => navigate("/rondas")} />

        {/* Status da próxima ronda */}
        <div className="flex-1 min-h-0 rounded-2xl px-4 py-3 flex flex-col justify-center" style={{ background: "#ffffff", border: "1px solid #e8eaf0" }}>
          <div className="flex items-center gap-1.5 mb-2.5">
            <Clock className="w-3 h-3" style={{ color: "#94a3b8" }} />
            <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#94a3b8" }}>Monitoramento de ronda</p>
          </div>
          <div className="grid grid-cols-2">

            {/* Col 1 — Próxima ronda */}
            <div className="text-center" style={{ borderRight: "1px solid #f1f5f9" }}>
              <p className="text-[15px] font-bold leading-none mb-1 text-[#0f172a]">
                {!rondasIntel
                  ? "…"
                  : rondasIntel.semHistorico
                  ? "—"
                  : fmtHora(rondasIntel.proximaRondaAt.toISOString())}
              </p>
              <p className="text-[10px] font-medium" style={{ color: "#94a3b8" }}>Próx. ronda</p>
              {rondasIntel && !rondasIntel.semHistorico && rondasIntel.ultimaRondaAt && (
                <p className="text-[9px] mt-0.5" style={{ color: "#c4c9d6" }}>
                  Última {fmtHora(rondasIntel.ultimaRondaAt.toISOString())}
                </p>
              )}
            </div>

            {/* Col 2 — Status ronda */}
            <div className="text-center">
              {!rondasIntel ? (
                <p className="text-[15px] font-bold leading-none mb-1" style={{ color: "#94a3b8" }}>…</p>
              ) : rondasIntel.semHistorico ? (
                <p className="text-[11px] font-semibold leading-none mb-1" style={{ color: "#64748b" }}>1ª ronda</p>
              ) : rondaAtrasada ? (
                <p className="text-[11px] font-bold leading-none mb-1" style={{ color: "#ef4444" }}>⚠ Atrasada</p>
              ) : rondaRestanteMin !== null && rondaRestanteMin <= 0 ? (
                <p className="text-[13px] font-bold leading-none mb-1" style={{ color: "#f59e0b" }}>Agora!</p>
              ) : (
                <p className="text-[15px] font-bold leading-none mb-1" style={{ color: "#10b981" }}>
                  {rondaRestanteMin !== null ? `${rondaRestanteMin}min` : "—"}
                </p>
              )}
              <p className="text-[10px] font-medium" style={{ color: rondaAtrasada ? "#ef4444" : "#94a3b8" }}>Status</p>
            </div>

          </div>
        </div>

        {/*
          Ocultos temporariamente — reativar quando necessário:
          - Banner passagem pendente
          - Botão Iniciar / Encerrar Plantão
          - Card "Turno atual" (col Avisos)
          - MiniMetric Ocorrências
          - Card "Avisos recentes"
          - Card "Última passagem recebida"
        */}

      </div>

      {/* Modal de lista */}
      {modalLista && (
        <div
          className="fixed inset-0 z-[999] flex items-end justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
        >
          <div
            className="w-full max-w-md rounded-3xl overflow-hidden flex flex-col"
            style={{ maxHeight: "72vh", background: "#ffffff", border: "1px solid #e8eaf0" }}
          >
            <div className="flex-none px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #e8eaf0" }}>
              <p className="font-bold text-[15px] text-[#0f172a]">{titulosModal[modalLista]}</p>
              <button
                type="button"
                onClick={() => setModalLista(null)}
                className="w-8 h-8 flex items-center justify-center rounded-xl"
                style={{ background: "#f1f5f9" }}
              >
                <X className="w-4 h-4 text-[#64748b]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingLista ? (
                <p className="py-10 text-center text-[13px]" style={{ color: "#94a3b8" }}>Carregando…</p>
              ) : listaItems.length === 0 ? (
                <p className="py-10 text-center text-[13px]" style={{ color: "#94a3b8" }}>{vazioModal[modalLista]}</p>
              ) : (
                <div style={{ divide: "1px solid #e8eaf0" }}>
                  {listaItems.map((item) => {
                    if (modalLista === "rondas") return (
                      <div key={item.id} className="px-5 py-3.5 flex items-center gap-3" style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#f0fdf4" }}>
                          <Footprints className="w-4 h-4" style={{ color: "#10b981" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[13px] text-[#0f172a]">{item.ponto || "Ponto"}</p>
                          {isSupervisor && item.vigilante_nome && (
                            <p className="text-[11px]" style={{ color: "#94a3b8" }}>{item.vigilante_nome}</p>
                          )}
                        </div>
                        <p className="text-[11px] font-mono flex-shrink-0" style={{ color: "#94a3b8" }}>{fmtHora(item.created_at)}</p>
                      </div>
                    );
                    return (
                      <div key={item.id} className="px-5 py-3.5 flex items-center gap-3" style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#fffbeb" }}>
                          <AlertTriangle className="w-4 h-4" style={{ color: "#f59e0b" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[13px] text-[#0f172a] truncate">{item.titulo || item.categoria || "Ocorrência"}</p>
                          {isSupervisor && item.vigilante_nome && (
                            <p className="text-[11px]" style={{ color: "#94a3b8" }}>{item.vigilante_nome}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>{fmtHora(item.created_at)}</p>
                          {item.prioridade && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md mt-0.5 inline-block ${
                              item.prioridade === "alta"  ? "text-red-700 bg-red-50"    :
                              item.prioridade === "media" ? "text-amber-700 bg-amber-50" :
                              "text-slate-600 bg-slate-100"
                            }`}>{item.prioridade}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex-none px-4 py-4" style={{ borderTop: "1px solid #e8eaf0" }}>
              <button
                type="button"
                onClick={() => setModalLista(null)}
                className="w-full py-3.5 rounded-2xl font-bold text-[14px] text-white active:scale-[0.98] transition"
                style={{ background: "#08111F" }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}

const MiniMetric = ({ icon: Icon, label, value, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-2xl p-3 text-left active:scale-[0.97] transition-all w-full"
    style={
      danger
        ? { background: "#fff7ed", border: "1px solid #fed7aa" }
        : { background: "#ffffff", border: "1px solid #e8eaf0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }
    }
  >
    <div
      className="w-7 h-7 rounded-xl flex items-center justify-center mb-2"
      style={
        danger
          ? { background: "#fef3c7" }
          : { background: "#f1f5f9" }
      }
    >
      <Icon
        className="w-3.5 h-3.5"
        style={{ color: danger ? "#f97316" : "#334155" }}
      />
    </div>
    <p
      className="text-[18px] font-bold leading-none mb-1"
      style={{ color: danger ? "#ea580c" : "#0f172a" }}
    >
      {value}
    </p>
    <p
      className="text-[11px] font-medium"
      style={{ color: danger ? "#f97316" : "#64748b" }}
    >
      {label}
    </p>
  </button>
);
